/**
 * Design tokens.
 *
 * The whole UI is styled with inline style objects, so this module is the only
 * place a colour, radius or shadow is defined. Change the brand here and it
 * propagates everywhere.
 *
 * The transactional email in `server/src/emails/` keeps its own copy of the
 * palette because email clients cannot import modules.
 */

export const color = {
  // Brand. `primary` is sampled from the logo in brand/crave-logo.png — keep
  // the two in step, and re-run `npm run brand:build` if the logo changes.
  primary: '#405C1C',
  primaryDark: '#2F4514',
  primarySoft: '#F1F5E9',
  primaryBorder: '#D5E0C2',

  // Neutrals, light → dark
  canvas: '#EFEBE5',
  surface: '#FFFFFF',
  surfaceAlt: '#FAF7F2',
  surfaceSunken: '#F4F1EC',
  border: '#E9E5DE',
  borderStrong: '#D8D2C9',

  // Text
  ink: '#141414',
  inkMuted: '#6E6A64',
  inkFaint: '#A8A29A',
  inkInverse: '#FFFFFF',
  onDark: '#1B1A18',

  // Status
  success: '#1FA971',
  warning: '#F59E0B',
  danger: '#E23D2C',
  star: '#F5A623',
};

export const radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 18,
  sheet: 22,
  /**
   * Swipe cards sit a step rounder than `xl` — closer to how iOS rounds its own
   * large surfaces, which reads as native rather than ported. Kept in step with
   * `radius.card` in mobile/src/theme/tokens.ts.
   */
  card: 26,
  phone: 44,
  pill: 999,
};

export const shadow = {
  card: '0 6px 28px rgba(0,0,0,0.12), 0 1px 6px rgba(0,0,0,0.05)',
  raised: '0 2px 12px rgba(0,0,0,0.10)',
  sheet: '0 8px 32px rgba(0,0,0,0.14)',
  phone: '0 0 0 1px rgba(0,0,0,0.06), 0 28px 72px rgba(0,0,0,0.20)',
  brand: '0 8px 24px rgba(64,92,28,0.34)',
};

export const font = {
  stack: "-apple-system,BlinkMacSystemFont,'SF Pro Text','Segoe UI',sans-serif",
  size: { xs: 9, sm: 11, md: 13, lg: 15, xl: 18, display: 24 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800, black: 900 },
};

/** Layout constants that several components need to agree on. */
export const layout = {
  /** Width of the simulated phone. Above this the desktop frame appears. */
  phoneWidth: 390,
  /** Height of the framed phone on desktop, capped to the viewport. */
  phoneHeight: 844,
  /** Below this viewport width the app fills the screen instead of framing it. */
  phoneBreakpoint: 430,
};

/** Uppercase micro-label used above nearly every section. */
export const overline = {
  fontSize: font.size.xs,
  fontWeight: font.weight.heavy,
  textTransform: 'uppercase',
  letterSpacing: '1.3px',
  color: color.inkFaint,
  margin: 0,
};

/**
 * The shared web brand field. It starts with the exact green sampled from the
 * logo, then adds only a restrained light lift so a full viewport does not read
 * as a flat export swatch.
 *
 * Exported because the public landing page, restaurant dashboard and admin
 * shell all use this as the material under their chrome. Pair it with
 * `className="grain"`; the colour and texture are one material.
 */
export const brandPlane = `radial-gradient(120% 80% at 50% -10%, rgba(247,244,237,0.16) 0%, rgba(247,244,237,0) 62%),
                           radial-gradient(90% 70% at 100% 0%, rgba(47,69,20,0.18) 0%, rgba(47,69,20,0) 58%),
                           #405C1C`;

/**
 * Type and controls placed directly on {@link brandPlane}.
 *
 * The dashboard first tried to solve contrast by standing a white sheet on the
 * green, which turned one surface into a stack of boxes — the working area read
 * as a modal parked on a marketing page. On the brand plane the contrast is the
 * type, not a panel behind it: cream text, and fields that are a tint of the
 * ground rather than a different material sitting on it.
 *
 * These are the values the landing page's waitlist form already uses, named
 * here so the two cannot drift the way the three copies of the gradient did.
 */
export const onBrand = {
  /** Primary type. The same off-white the landing page is written in. */
  text: '#F7F4ED',
  /** Secondary type — labels, hints, anything supporting. */
  textMuted: 'rgba(247,244,237,0.78)',
  /** Tertiary type — captions, counts, placeholder-weight information. */
  textFaint: 'rgba(247,244,237,0.55)',

  /** Input and control fill: a tint of the ground, not a new material. */
  field: 'rgba(255,255,255,0.14)',
  fieldHover: 'rgba(255,255,255,0.19)',
  border: 'rgba(247,244,237,0.46)',
  borderSoft: 'rgba(247,244,237,0.24)',
  focusRing: 'rgba(247,244,237,0.22)',

  /** A solid control, for the one action per screen that deserves the weight. */
  solid: '#F7F4ED',
  solidInk: '#14150F',
};
